import React, { FC, memo, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { IconProps } from '../../assets/icons/icon.types';
import { DropDown } from '../dropdown/index';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../hover-card/index';
import { createDropdownStore } from './side-bar.store';

export type SideBarItemProps = {
  className?: string;
  Icon: FC<IconProps>;
  isExpanded: boolean;
  isSelected: boolean;
  onClick?: () => void;
  specialIconColor?: string;
  specialTitleColor?: string;
  subContent?: ReactNode;
  title: string;
};

export const SideBarItem: FC<SideBarItemProps> = memo(
  ({
    Icon,
    isExpanded,
    isSelected,
    onClick,
    title,
    className,
    specialIconColor,
    specialTitleColor,
    subContent,
  }) => {
    const disabled = !subContent || !isExpanded;

    const dropdownStore = disabled ? undefined : createDropdownStore(title);

    const isOpen = dropdownStore?.(({ isOpen }) => isOpen);
    const toggler = dropdownStore?.(({ toggler }) => toggler);

    const Wrapper: FC<{ children: ReactNode }> = ({ children }) =>
      subContent && !isExpanded ? (
        <HoverCard>
          <HoverCardTrigger>{children}</HoverCardTrigger>

          <HoverCardContent
            align="start"
            className="rounded-md border border-gray-200 bg-gray-25 p-2 shadow-3xl"
            isShownTriangle={false}
            side="right"
          >
            {subContent}
          </HoverCardContent>
        </HoverCard>
      ) : (
        <>{children}</>
      );

    return (
      <DropDown
        answer={subContent}
        className={{
          questionLabelWrapper: 'p-0',
          questionIcon: twMerge(!isExpanded && 'hidden'),
          questionLabel: 'w-full text-sm text-[#2565C8]',
          iconColor: isSelected
            ? {
                closed: '#2565C8',
                open: '#2565C8',
              }
            : {
                closed: '#98A2B3',
                open: '#98A2B3',
              },
          wrapper: twMerge(
            'w-full p-0',
            isSelected && 'rounded-lg bg-white shadow-sm',
            isExpanded && 'pr-1',
          ),
          answerWrapper: 'mt-0',
        }}
        defaultValue={isOpen}
        disabled={disabled}
        onStateChange={toggler}
        question={
          <Wrapper>
            <div
              className={twMerge(
                'flex w-10 cursor-pointer items-center gap-[10px] transition-all duration-300',
                isExpanded && 'w-full pr-8',
                className,
              )}
              onClick={() => !subContent && onClick?.()}
            >
              <div className={twMerge('flex size-10 shrink-0 items-center justify-center')}>
                {Icon ? (
                  <Icon
                    height={20}
                    iconColor={
                      specialIconColor ? specialIconColor : isSelected ? '#2565C8' : '#98A2B3'
                    }
                    width={20}
                  />
                ) : null}
              </div>
              <span
                className={twMerge(
                  'w-fit overflow-hidden text-nowrap text-sm font-[450] text-gray-400',
                  specialTitleColor ? specialTitleColor : isSelected && 'text-brand-700',
                )}
              >
                {title}
              </span>
            </div>
          </Wrapper>
        }
      />
    );
  },
);

SideBarItem.displayName = 'SideBarItem';
